import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import {
  Material, Recipe, Monster,
  CreateMaterialData, CreateRecipeData, CreateMonsterData,
  ContentTab, AnyContent,
} from '../types/adminContent';
import ContentDetailsModal from './ContentDetailsModal';
import CreateMaterialModal from './CreateMaterialModal';
import CreateMonsterModal from './CreateMonsterModal';
import CreateRecipeModal from './CreateRecipeModal';
import { useConfirm } from './ConfirmModal';
import './AdminContentManager.css';

const DEFAULT_MATERIAL: CreateMaterialData = {
  name: '', description: '', rarity: 'Common', iconName: '',
  materialType: 'Wood', baseValue: 1, isActive: true,
};

const DEFAULT_RECIPE: CreateRecipeData = {
  name: '', description: '', category: 'Weapon', requiredLevel: 1,
  craftingTime: 30, baseSuccessRate: 80, craftedItemId: 0, isActive: true,
  ingredients: [], minDamage: 1, maxDamage: 3, minRange: 1, maxRange: 1,
  damageType: 'Physical', aoeShape: 'Single', requiresLoS: true,
  weaponAPCost: 1, accuracyModifier: 0, usesAmmo: false, maxAmmo: 0,
  equipmentSlot: null, equipSpritePath: null,
};

const DEFAULT_MONSTER: CreateMonsterData = {
  name: '', description: '', level: 1, health: 100, damage: 10,
  defense: 5, speed: 5, aiPersonality: 'Aggressive', monsterType: 'Beast',
  imageUrl: null, isActive: true,
};

const getRarityColor = (rarity: string): string => {
  switch (rarity) {
    case 'Common':    return '#95a5a6';
    case 'Uncommon':  return '#27ae60';
    case 'Rare':      return '#3498db';
    case 'Epic':      return '#9b59b6';
    case 'Legendary': return '#f39c12';
    default:          return '#95a5a6';
  }
};

const AdminContentManager: React.FC = () => {
  const { handleAuthError } = useAuth();
  const { confirm, confirmDialog } = useConfirm();
  const [activeTab, setActiveTab] = useState<ContentTab>('materials');
  const [materials, setMaterials] = useState<Material[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [monsters, setMonsters] = useState<Monster[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedItem, setSelectedItem] = useState<AnyContent | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingMonsterId, setEditingMonsterId] = useState<number | null>(null);
  const [editingRecipeId, setEditingRecipeId] = useState<number | null>(null);

  const [materialFormData, setMaterialFormData] = useState<CreateMaterialData>(DEFAULT_MATERIAL);
  const [recipeFormData, setRecipeFormData] = useState<CreateRecipeData>(DEFAULT_RECIPE);
  const [monsterFormData, setMonsterFormData] = useState<CreateMonsterData>(DEFAULT_MONSTER);

  const fetchMaterials = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/materials');
      if (response.ok) {
        const data = await response.json();
        setMaterials(data.materials || []);
      } else {
        setError('Failed to load materials');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError('Error loading materials');
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  const fetchRecipes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/recipes');
      if (response.ok) {
        const data = await response.json();
        setRecipes(data.recipes || []);
      } else {
        setError('Failed to load recipes');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError('Error loading recipes');
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  const fetchMonsters = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/monsters');
      if (response.ok) {
        const data = await response.json();
        setMonsters(data.monsters || []);
      } else {
        setError('Failed to load monsters');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError('Error loading monsters');
    } finally {
      setIsLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (activeTab === 'materials') fetchMaterials();
    else if (activeTab === 'recipes') { fetchRecipes(); fetchMaterials(); } // materials needed for the ingredient picker
    else if (activeTab === 'monsters') fetchMonsters();
  }, [activeTab, fetchMaterials, fetchRecipes, fetchMonsters]);

  const handleCreateMaterial = async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/materials', {
        method: 'POST',
        body: JSON.stringify(materialFormData),
      });
      if (response.ok) {
        setShowCreateModal(false);
        setMaterialFormData(DEFAULT_MATERIAL);
        await fetchMaterials();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create material');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError('Error creating material');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRecipe = async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/recipes', {
        method: 'POST',
        body: JSON.stringify(recipeFormData),
      });
      if (response.ok) {
        setShowCreateModal(false);
        setRecipeFormData(DEFAULT_RECIPE);
        await fetchRecipes();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create recipe');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError('Error creating recipe');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRecipe = async () => {
    if (editingRecipeId == null) return;
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/admin/content/recipes/${editingRecipeId}`, {
        method: 'PUT',
        body: JSON.stringify(recipeFormData),
      });
      if (response.ok) {
        setShowCreateModal(false);
        setEditingRecipeId(null);
        setRecipeFormData(DEFAULT_RECIPE);
        await fetchRecipes();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update recipe');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError('Error updating recipe');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateMonster = async () => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest('/admin/content/monsters', {
        method: 'POST',
        body: JSON.stringify(monsterFormData),
      });
      if (response.ok) {
        setShowCreateModal(false);
        setMonsterFormData(DEFAULT_MONSTER);
        await fetchMonsters();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create monster');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError('Error creating monster');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateMonster = async () => {
    if (editingMonsterId == null) return;
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/admin/content/monsters/${editingMonsterId}`, {
        method: 'PUT',
        body: JSON.stringify(monsterFormData),
      });
      if (response.ok) {
        setShowCreateModal(false);
        setEditingMonsterId(null);
        setMonsterFormData(DEFAULT_MONSTER);
        await fetchMonsters();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update monster');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError('Error updating monster');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (type: string, id: number, currentStatus: boolean) => {
    try {
      const response = await makeAuthenticatedRequest(`/admin/content/${type}/${id}/toggle`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !currentStatus }),
      });
      if (response.ok) {
        if (type === 'materials') await fetchMaterials();
        else if (type === 'recipes') await fetchRecipes();
        else if (type === 'monsters') await fetchMonsters();
        setError(null);
      } else {
        setError(`Failed to update ${type} status`);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError(`Error updating ${type}`);
    }
  };

  const handleDeleteItem = async (type: string, id: number) => {
    const confirmed = await confirm({
      title: `Delete ${type.slice(0, -1)}`,
      message: `Are you sure you want to delete this ${type.slice(0, -1)}? This action cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!confirmed) return;
    try {
      const response = await makeAuthenticatedRequest(`/admin/content/${type}/${id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        if (type === 'materials') await fetchMaterials();
        else if (type === 'recipes') await fetchRecipes();
        else if (type === 'monsters') await fetchMonsters();
        setError(null);
      } else {
        setError(`Failed to delete ${type.slice(0, -1)}`);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) { handleAuthError(); return; }
      setError(`Error deleting ${type.slice(0, -1)}`);
    }
  };

  const openCreateModal = () => {
    if (activeTab === 'monsters') {
      setEditingMonsterId(null);
      setMonsterFormData(DEFAULT_MONSTER);
    } else if (activeTab === 'recipes') {
      setEditingRecipeId(null);
      setRecipeFormData(DEFAULT_RECIPE);
    }
    setShowCreateModal(true);
  };
  const openEditMonsterModal = (monster: Monster) => {
    setEditingMonsterId(monster.id);
    setMonsterFormData({
      name: monster.name,
      description: monster.description,
      level: monster.level,
      health: monster.health,
      damage: monster.damage,
      defense: monster.defense,
      speed: monster.speed,
      aiPersonality: monster.aiPersonality,
      monsterType: monster.monsterType,
      imageUrl: monster.imageUrl ?? null,
      isActive: monster.isActive,
    });
    setShowCreateModal(true);
  };
  const openEditRecipeModal = (recipe: Recipe) => {
    setEditingRecipeId(recipe.id);
    setRecipeFormData({
      ...DEFAULT_RECIPE,
      name: recipe.name,
      description: recipe.description,
      category: recipe.category,
      requiredLevel: recipe.requiredLevel,
      craftingTime: recipe.craftingTime,
      baseSuccessRate: recipe.baseSuccessRate,
      isActive: recipe.isActive,
      equipmentSlot: recipe.resultEquipmentSlot ?? null,
      equipSpritePath: recipe.equipSpritePath ?? null,
      ingredients: recipe.ingredients.map(i => ({ materialId: i.materialId, quantity: i.quantity })),
    });
    setShowCreateModal(true);
  };
  const openDetailsModal = (item: AnyContent) => { setSelectedItem(item); setShowDetailsModal(true); };

  const tabLabel = activeTab === 'materials' ? 'Material' : activeTab === 'recipes' ? 'Recipe' : 'Monster';

  if (isLoading && materials.length === 0 && recipes.length === 0 && monsters.length === 0) {
    return (
      <div className="content-manager">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-manager">
      {confirmDialog}
      <div className="content-header">
        <div className="content-title">
          <h2>📦 Content Management</h2>
          <p>Manage game materials, recipes, and monsters</p>
        </div>
        <button className="create-content-button" onClick={openCreateModal}>
          ✨ Create {tabLabel}
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="content-tabs">
        <button
          className={`content-tab-btn ${activeTab === 'materials' ? 'active' : ''}`}
          onClick={() => setActiveTab('materials')}
        >
          🪨 Materials ({materials.length})
        </button>
        <button
          className={`content-tab-btn ${activeTab === 'recipes' ? 'active' : ''}`}
          onClick={() => setActiveTab('recipes')}
        >
          📜 Recipes ({recipes.length})
        </button>
        <button
          className={`content-tab-btn ${activeTab === 'monsters' ? 'active' : ''}`}
          onClick={() => setActiveTab('monsters')}
        >
          👹 Monsters ({monsters.length})
        </button>
      </div>

      {/* Materials Tab */}
      {activeTab === 'materials' && (
        <div className="content-table-container">
          <table className="content-table">
            <thead>
              <tr>
                <th>Material</th><th>Type</th><th>Rarity</th><th>Value</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {materials.map(material => (
                <tr key={material.id} className="content-row">
                  <td className="content-info">
                    <div className="content-name">{material.name}</div>
                    <div className="content-description">{material.description}</div>
                    <div className="content-icon">🎯 {material.iconName}</div>
                  </td>
                  <td><span className="type-badge">{material.materialType}</span></td>
                  <td>
                    <span className="rarity-badge" style={{ backgroundColor: getRarityColor(material.rarity) }}>
                      {material.rarity}
                    </span>
                  </td>
                  <td className="value-cell">💰 {material.baseValue}</td>
                  <td>
                    <span className={`status-badge ${material.isActive ? 'active' : 'inactive'}`}>
                      {material.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="content-actions">
                    <button className="view-button" onClick={() => openDetailsModal(material)}>👁️ View</button>
                    <button
                      className={`toggle-button ${material.isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus('materials', material.id, material.isActive)}
                    >
                      {material.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button className="delete-button" onClick={() => handleDeleteItem('materials', material.id)}>🗑️ Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {materials.length === 0 && (
            <div className="empty-state">
              <h3>No materials found</h3>
              <p>Create your first material to get started!</p>
              <button className="create-content-button" onClick={openCreateModal}>✨ Create Material</button>
            </div>
          )}
        </div>
      )}

      {/* Recipes Tab */}
      {activeTab === 'recipes' && (
        <div className="content-table-container">
          <table className="content-table">
            <thead>
              <tr>
                <th>Recipe</th><th>Category</th><th>Level Req</th><th>Success Rate</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recipes.map(recipe => (
                <tr key={recipe.id} className="content-row">
                  <td className="content-info">
                    <div className="content-name">{recipe.name}</div>
                    <div className="content-description">{recipe.description}</div>
                    <div className="recipe-details">⏱️ {recipe.craftingTime}s • 📦 {recipe.ingredients.length} ingredients</div>
                  </td>
                  <td><span className="type-badge">{recipe.category}</span></td>
                  <td className="level-cell">🎯 {recipe.requiredLevel}</td>
                  <td className="success-rate-cell">✅ {recipe.baseSuccessRate}%</td>
                  <td>
                    <span className={`status-badge ${recipe.isActive ? 'active' : 'inactive'}`}>
                      {recipe.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="content-actions">
                    <button className="view-button" onClick={() => openDetailsModal(recipe)}>👁️ View</button>
                    <button className="view-button" onClick={() => openEditRecipeModal(recipe)}>✏️ Edit</button>
                    <button
                      className={`toggle-button ${recipe.isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus('recipes', recipe.id, recipe.isActive)}
                    >
                      {recipe.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button className="delete-button" onClick={() => handleDeleteItem('recipes', recipe.id)}>🗑️ Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {recipes.length === 0 && (
            <div className="empty-state">
              <h3>No recipes found</h3>
              <p>Create your first recipe to get started!</p>
              <button className="create-content-button" onClick={openCreateModal}>✨ Create Recipe</button>
            </div>
          )}
        </div>
      )}

      {/* Monsters Tab */}
      {activeTab === 'monsters' && (
        <div className="content-table-container">
          <table className="content-table">
            <thead>
              <tr>
                <th>Monster</th><th>Level</th><th>Stats</th><th>AI</th><th>Status</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {monsters.map(monster => (
                <tr key={monster.id} className="content-row">
                  <td className="content-info">
                    <div className="content-name">
                      {monster.imageUrl && (
                        <img className="monster-sprite-thumb" src={monster.imageUrl} alt="" />
                      )}
                      {monster.name}
                    </div>
                    <div className="content-description">{monster.description}</div>
                    <div className="monster-type">👹 {monster.monsterType}</div>
                  </td>
                  <td className="level-cell">🎯 {monster.level}</td>
                  <td className="monster-stats">
                    <div>❤️ {monster.health} HP</div>
                    <div>⚔️ {monster.damage} ATK</div>
                    <div>🛡️ {monster.defense} DEF</div>
                    <div>💨 {monster.speed} SPD</div>
                  </td>
                  <td><span className="ai-badge">{monster.aiPersonality}</span></td>
                  <td>
                    <span className={`status-badge ${monster.isActive ? 'active' : 'inactive'}`}>
                      {monster.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="content-actions">
                    <button className="view-button" onClick={() => openDetailsModal(monster)}>👁️ View</button>
                    <button className="view-button" onClick={() => openEditMonsterModal(monster)}>✏️ Edit</button>
                    <button
                      className={`toggle-button ${monster.isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus('monsters', monster.id, monster.isActive)}
                    >
                      {monster.isActive ? 'Disable' : 'Enable'}
                    </button>
                    <button className="delete-button" onClick={() => handleDeleteItem('monsters', monster.id)}>🗑️ Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {monsters.length === 0 && (
            <div className="empty-state">
              <h3>No monsters found</h3>
              <p>Create your first monster to get started!</p>
              <button className="create-content-button" onClick={openCreateModal}>✨ Create Monster</button>
            </div>
          )}
        </div>
      )}

      {showCreateModal && activeTab === 'materials' && (
        <CreateMaterialModal
          formData={materialFormData}
          onChange={setMaterialFormData}
          onSubmit={handleCreateMaterial}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showCreateModal && activeTab === 'recipes' && (
        <CreateRecipeModal
          title={editingRecipeId != null ? 'Edit Recipe' : 'Create New Recipe'}
          submitLabel={editingRecipeId != null ? 'Save Recipe' : 'Create Recipe'}
          formData={recipeFormData}
          materials={materials}
          onChange={setRecipeFormData}
          onSubmit={editingRecipeId != null ? handleUpdateRecipe : handleCreateRecipe}
          onClose={() => { setShowCreateModal(false); setEditingRecipeId(null); }}
        />
      )}

      {showCreateModal && activeTab === 'monsters' && (
        <CreateMonsterModal
          title={editingMonsterId != null ? 'Edit Monster' : 'Create New Monster'}
          submitLabel={editingMonsterId != null ? 'Save Monster' : 'Create Monster'}
          formData={monsterFormData}
          onChange={setMonsterFormData}
          onSubmit={editingMonsterId != null ? handleUpdateMonster : handleCreateMonster}
          onClose={() => { setShowCreateModal(false); setEditingMonsterId(null); }}
        />
      )}

      {showDetailsModal && selectedItem && (
        <ContentDetailsModal
          item={selectedItem}
          activeTab={activeTab}
          getRarityColor={getRarityColor}
          onClose={() => setShowDetailsModal(false)}
        />
      )}
    </div>
  );
};

export default AdminContentManager;
